"use client"

import { useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import { REPORT_TO } from "@/lib/breach/directory/report-to"
import { OBLIGATIONS } from "@/lib/breach/deadlines"
import { Chips, Contact, NeedsCheck, OfficialLinks, SearchBox, useHashTarget } from "./directory-ui"

const CATS = [
  { id: "all", label: "All" }, { id: "law-enforcement", label: "Law enforcement" }, { id: "regulator", label: "Regulators" },
  { id: "industry", label: "Industry" }, { id: "state", label: "State and other" },
] as const
type Cat = (typeof CATS)[number]["id"]
const regime = (id: string) => OBLIGATIONS.find((o) => o.id === id)?.regime ?? id

export function ReportDirectory({ anchor }: { anchor?: string } = {}) {
  const [q, setQ] = useState("")
  const [cat, setCat] = useState<Cat>("all")
  const target = useHashTarget(anchor)
  const list = useMemo(() => {
    const n = q.trim().toLowerCase()
    return REPORT_TO.filter((e) => (cat === "all" || e.category === cat || (cat === "state" && e.category === "other")) && (!n || `${e.name} ${e.agency} ${e.whatFor}`.toLowerCase().includes(n)))
  }, [q, cat])
  return (
    <div>
      <SearchBox value={q} onChange={setQ} placeholder="Search by agency or purpose (for example HIPAA, bank, FBI)" count={list.length} total={REPORT_TO.length} />
      <Chips options={[...CATS]} value={cat} onChange={setCat} label="Filter by type" />
      {list.length === 0 && <p className="mt-6 rounded-lg border border-border p-4 text-sm text-muted-foreground">Nothing matches. Try fewer words, or ask your lawyer who you need to notify.</p>}
      <ul className="mt-6 grid gap-4">
        {list.map((e) => (
          <li key={e.id} id={e.id} className={cn("scroll-mt-6 rounded-lg border p-4", target === e.id ? "border-accent bg-accent/10" : "border-border")}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-lg font-semibold">{e.name}</h3>
              <span className="text-xs text-muted-foreground">{e.agency}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{e.whatFor}</p>
            {e.whoShouldReport && <p className="mt-2 text-sm"><strong>Who reports here:</strong> <span className="text-muted-foreground">{e.whoShouldReport}</span></p>}
            <OfficialLinks links={e.urls} />
            <Contact phone={e.phone} email={e.email} />
            {e.notes && <p className="mt-2 text-xs text-muted-foreground">{e.notes}</p>}
            {e.clockIds.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">Deadline clocks served: {e.clockIds.map(regime).join(" · ")}</p>
            )}
            <NeedsCheck show={e.needsCheck} />
          </li>
        ))}
      </ul>
    </div>
  )
}
